import type { PlayerProfile } from "./types";

const CACHE_PREFIX = "sidequest-player-profile:v1:";
type CachedPlayerProfile = { profile: PlayerProfile | null };

function cacheKey(uid: string) {
  return `${CACHE_PREFIX}${encodeURIComponent(uid)}`;
}

function isProfile(value: unknown): value is PlayerProfile {
  if (!value || typeof value !== "object") return false;
  const profile = value as Record<string, unknown>;
  return typeof profile.name === "string" && profile.name.length <= 28
    && typeof profile.avatar === "string" && profile.avatar.length <= 2000
    && typeof profile.bio === "string" && profile.bio.length <= 240
    && (profile.updatedAt === null || (typeof profile.updatedAt === "number" && Number.isFinite(profile.updatedAt)));
}

// This is display data only. Callers must restore the real Firebase user before
// selecting a cache entry; a cached profile never establishes a signed-in session.
export function readCachedPlayerProfile(uid: string): CachedPlayerProfile | null {
  try {
    const raw = localStorage.getItem(cacheKey(uid));
    if (!raw) return null;
    const cached: unknown = JSON.parse(raw);
    if (!cached || typeof cached !== "object") return null;
    const value = cached as Record<string, unknown>;
    if (value.uid !== uid || !(value.profile === null || isProfile(value.profile))) return null;
    return { profile: value.profile };
  } catch {
    return null;
  }
}

export function cachePlayerProfile(uid: string, profile: PlayerProfile | null): void {
  try {
    localStorage.setItem(cacheKey(uid), JSON.stringify({ uid, profile }));
  } catch {
    // Disabled/full browser storage must not prevent using the fetched profile.
  }
}

export function clearPlayerProfileCache(uid?: string): void {
  try {
    if (uid) {
      localStorage.removeItem(cacheKey(uid));
      return;
    }
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(CACHE_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // Authentication must keep working even when browser storage is unavailable.
  }
}
